import React, { useState, useEffect } from 'react';
import { Trash2, Edit, Eye, EyeOff, Copy, Mail, Phone, Import as Passport, ExternalLink, Download, FileImage } from 'lucide-react';
import { useGuidesStore } from '../../stores/guidesStore';
import { Guide } from '../../types/guide';
import { useOrganizersStore } from '../../stores/organizersStore';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  guide: Guide;
  onEdit: (guide: Guide) => void;
  onDelete: (guide: Guide) => void;
};

export const GuideRow = ({ guide, onEdit, onDelete }: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const { organizers, fetchOrganizers } = useOrganizersStore();
  const [organizerName, setOrganizerName] = useState<string>('');
  const { translations } = useLanguage();

  // Fetch organizer details when component mounts
  useEffect(() => {
    const loadOrganizerDetails = async () => {
      if (!organizers[guide.organizerId]) {
        await fetchOrganizers();
      }
      const organizer = organizers[guide.organizerId];
      setOrganizerName(organizer?.name || translations?.guideRow?.undefinedOrganizer || 'Undefined');
    };

    loadOrganizerDetails();
  }, [guide.organizerId, organizers, fetchOrganizers, translations]);

  const copyToClipboard = async (text: string, type: 'email' | 'password' | 'passport' | 'nusukEmail' | 'mainPhone' | 'hajjPhone') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedGuide(text);
      setTimeout(() => setCopiedGuide(null), 2000);
      toast.success(translations?.bulkTransfer?.copySuccess || 'Copied successfully');
    } catch (error) {
      toast.error(translations?.bulkTransfer?.copyError || 'Failed to copy');
    }
  };

  const [copiedGuide, setCopiedGuide] = useState<string | null>(null);
  
  // Helper function to extract filename from URL
  const getFilenameFromUrl = (url: string) => {
    try {
      const pathSegments = new URL(url).pathname.split('/');
      const filename = pathSegments[pathSegments.length - 1];
      const match = filename.match(/passport_([^.]+)\.(.+)$/);
      if (match && match.length >= 3) {
        return `${match[1]}.${match[2]}`;
      }
      return filename;
    } catch (error) {
      return 'passport-photo';
    }
  };
  
  // Download passport photo
  const downloadPassportPhoto = () => {
    if (!guide.passportPhotoUrl) {
      toast.error('No passport photo available');
      return;
    }
    
    const link = document.createElement('a');
    link.href = guide.passportPhotoUrl;
    
    const filename = guide.passportNumber ? 
      `${guide.passportNumber}.${guide.passportPhotoUrl.split('.').pop()}` : 
      guide.name.replace(/\s+/g, '_') + '.jpg';
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Downloading passport photo');
  };

  // Mobile view
  const MobileView = () => (
    <div className="p-4 border-b md:hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
            <span className="text-primary-600 font-medium">{guide.name.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <span className="font-medium text-base">{guide.name}</span>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Building2 size={12} />
              {organizerName}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(guide)}
            className="p-2 text-primary-500 bg-primary-50 rounded-lg"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => onDelete(guide)}
            className="p-2 text-red-500 bg-red-50 rounded-lg"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 mt-3">
        <div className="bg-gray-50 p-2 rounded-lg flex flex-col">
          <div className="text-xs text-gray-500 mb-1">Email</div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{guide.email}</span>
            <button
              onClick={() => copyToClipboard(guide.email, 'email')}
              className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
            >
              <Copy size={14} className="text-gray-500" />
            </button>
          </div>
        </div>

        {guide.passportNumber && (
          <div className="bg-gray-50 p-2 rounded-lg flex flex-col">
            <div className="text-xs text-gray-500 mb-1">Passport</div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{guide.passportNumber}</span>
              <button
                onClick={() => copyToClipboard(guide.passportNumber!, 'passport')}
                className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
              >
                <Copy size={14} className="text-gray-500" />
              </button>
            </div>
          </div>
        )}

        {guide.nusukEmail && (
          <div className="bg-gray-50 p-2 rounded-lg flex flex-col">
            <div className="text-xs text-gray-500 mb-1">Nusuk Email</div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate max-w-[200px]">{guide.nusukEmail}</span>
              <button
                onClick={() => copyToClipboard(guide.nusukEmail!, 'nusukEmail')}
                className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
              >
                <Copy size={14} className="text-gray-500" />
              </button>
            </div>
          </div>
        )}
        
        {guide.mainPhone && (
          <div className="bg-gray-50 p-2 rounded-lg flex flex-col">
            <div className="text-xs text-gray-500 mb-1">Main Phone</div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{guide.mainPhone}</span>
              <button
                onClick={() => copyToClipboard(guide.mainPhone!, 'mainPhone')}
                className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
              >
                <Copy size={14} className="text-gray-500" />
              </button>
            </div>
          </div>
        )}
        
        {guide.hajjPhone && (
          <div className="bg-gray-50 p-2 rounded-lg flex flex-col">
            <div className="text-xs text-gray-500 mb-1">Hajj Phone</div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{guide.hajjPhone}</span>
              <button
                onClick={() => copyToClipboard(guide.hajjPhone!, 'hajjPhone')}
                className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
              >
                <Copy size={14} className="text-gray-500" />
              </button>
            </div>
          </div>
        )}

        {/* Passport Photo */}
        {guide.passportPhotoUrl && (
          <div className="bg-gray-50 p-2 rounded-lg flex flex-col">
            <div className="text-xs text-gray-500 mb-1">Passport Photo</div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate max-w-[200px]">
                {getFilenameFromUrl(guide.passportPhotoUrl)}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => window.open(guide.passportPhotoUrl, '_blank')}
                  className="p-1.5 text-primary-500 hover:bg-primary-50 rounded-full transition-colors"
                  title="View Passport Photo"
                >
                  <ExternalLink size={14} />
                </button>
                <button
                  onClick={downloadPassportPhoto}
                  className="p-1.5 text-primary-500 hover:bg-primary-50 rounded-full transition-colors"
                  title="Download Passport Photo"
                >
                  <Download size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // For desktop/laptop view, we now use a more integrated implementation in GuidesList
  return <MobileView />;
};

import { Building2 } from 'lucide-react';